import { useState } from 'react';
import { Button, Input, MessageBarBody, MessageBarTitle, ToggleButton } from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { CurrencySelectorComponent } from '../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import { onFormatDate, parseDateFromString } from '../../commons/component/Inputs/DateTimeInput/DateTimeInputfunctions';
import { GuideDrawerSection } from '../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../theme/components/GuideMessageBar';
import { initialsOf } from './General/Cashflow.Dashboard.Functions';
import { MEDICAL_CENTER_NAME } from './General/Cashflow.General.Functions';
import { parseAmount } from './CashFlow.Form.Logic';
import './CashFlow.Form.Styles.css';

/** Piezas comunes a los formularios de deuda y pago (dentro de un GuideDrawer). */

/** Paso del formulario: sección de la guía (título 16 Bold + ayuda) con el error del paso debajo. */
export const Step = ({ title, hint, error, children, testId }: {
    title: string;
    hint?: React.ReactNode;
    error?: string;
    children: React.ReactNode;
    testId?: string;
}) => (
    <GuideDrawerSection title={title} description={hint} testId={testId}>
        {children}
        {error && <div className="cff-error" role="alert">{error}</div>}
    </GuideDrawerSection>
);

/** Banner de la guía con el error del guardado (mensaje del API y, si vienen, los detalles). */
export const SaveErrorBanner = ({ title, message, items }: { title: React.ReactNode; message?: React.ReactNode; items?: string[] }) => (
    <GuideMessageBar intent="error" layout="multiline" role="alert">
        <MessageBarBody>
            <MessageBarTitle>{title}</MessageBarTitle>
            {message}
            {items && items.length > 0 && <ul className="cff-list">{items.map((item) => <li key={item}>{item}</li>)}</ul>}
        </MessageBarBody>
    </GuideMessageBar>
);

/**
 * Una parte que ya se conoce: se muestra, no se pide. `square` la dibuja como tarjeta rectangular
 * (radio 4 px, como los campos de la guía) con una línea secundaria opcional ("Profesional", "Persona o empresa").
 */
export const PartyChip = ({ role, name, square, detail }: { role: string; name: string; square?: boolean; detail?: string }) => (
    <span className={`cff-party${square ? ' cff-party--square' : ''}`} title={name} data-testid={square ? 'payment-party-card' : undefined}>
        <span className={`cff-party__avatar${name === MEDICAL_CENTER_NAME ? ' cff-party__avatar--me' : ''}`} aria-hidden>{initialsOf(name)}</span>
        <span className="cff-party__meta">
            <span className="cff-party__role">{role}</span>
            <span className="cff-party__name">{name}</span>
            {detail && <span className="cff-party__detail">{detail}</span>}
        </span>
    </span>
);

/** Chips con las monedas probables y "Otra…" para el resto. */
export const CurrencyChips = ({ suggested, value, onChange, disabled }: {
    suggested: string[];
    value?: string;
    onChange: (currency: string) => void;
    disabled?: boolean;
}) => {
    const [other, setOther] = useState(false);
    const chips = value && !suggested.includes(value) ? [...suggested, value] : suggested;

    if (disabled) {
        return (
            <div className="cff-chips">
                <ToggleButton shape="circular" checked disabled>{value}</ToggleButton>
            </div>
        );
    }

    return (
        <div className="cff-chips">
            {chips.map((code) => (
                <ToggleButton key={code} shape="circular" checked={value === code} onClick={() => { setOther(false); onChange(code); }}>
                    {code}
                </ToggleButton>
            ))}
            {!other && <Button appearance="transparent" onClick={() => setOther(true)}>Otra…</Button>}
            {other && (
                <div className="cff-chips__other">
                    <CurrencySelectorComponent
                        errorMessage={undefined}
                        defaultSelected={undefined}
                        label={undefined}
                        onCurrencyChange={(code) => code && onChange(code)}
                    />
                </div>
            )}
        </div>
    );
};

/** Importe con la moneda adelante. Acepta coma o punto decimal. Va dentro de un GuideField. */
export const AmountInput = ({ value, currency, onChange, autoFocus }: {
    value: string;
    currency?: string;
    onChange: (text: string, amount: number) => void;
    autoFocus?: boolean;
}) => (
    <Input
        className="cff-amount"
        inputMode="decimal"
        placeholder="0,00"
        value={value}
        autoFocus={autoFocus}
        contentBefore={<span className="cff-amount__cur">{currency || '—'}</span>}
        onChange={(_event, data) => onChange(data.value, parseAmount(data.value))}
    />
);

/** Selector de fecha (v9) para usar dentro de un GuideField: normaliza a medianoche como DateTimeInput. */
export const DateInput = ({ value, onChange, placeholder = 'Fecha' }: {
    value: Date | undefined;
    onChange: (date: Date | undefined) => void;
    placeholder?: string;
}) => (
    <DatePicker
        allowTextInput
        value={value}
        placeholder={placeholder}
        formatDate={onFormatDate}
        parseDateFromString={parseDateFromString}
        onSelectDate={(date) => {
            const midnight = date ? new Date(date).setHours(0, 0, 0, 0) : undefined;
            onChange(midnight ? new Date(midnight) : undefined);
        }}
    />
);

/** Sección plegada con lo que casi nunca hace falta. */
export const MoreData = ({ label = 'Más datos (opcional)', open: openProp, children }: {
    label?: string;
    open?: boolean;
    children: React.ReactNode;
}) => {
    const [open, setOpen] = useState(!!openProp);
    return (
        <>
            <div>
                <Button
                    appearance="transparent"
                    icon={open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    onClick={() => setOpen((current) => !current)}
                    aria-expanded={open}
                >
                    {label}
                </Button>
            </div>
            {open && children}
        </>
    );
};
